import { useState, useRef, useEffect, type ReactNode } from 'react';

interface EditableFieldProps {
  value: string;
  onChange: (value: string) => void;
  isEditMode: boolean;
  isDirty: boolean;
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
  tag?: keyof JSX.IntrinsicElements;
  children: ReactNode;
}

export function EditableField({
  value,
  onChange,
  isEditMode,
  isDirty,
  multiline = false,
  className = '',
  inputClassName = '',
  tag: Tag = 'div',
  children,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Place cursor at end
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  // Exit editing if edit mode is turned off
  useEffect(() => {
    if (!isEditMode) setIsEditing(false);
  }, [isEditMode]);

  if (isEditing && isEditMode) {
    const sharedClasses = `w-full rounded border border-green-400 bg-white px-2 py-1 outline-none focus:ring-2 focus:ring-green-500 ${inputClassName}`;

    return multiline ? (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setIsEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsEditing(false);
        }}
        rows={4}
        className={`${sharedClasses} resize-y text-sm leading-relaxed text-gray-600`}
      />
    ) : (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setIsEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsEditing(false);
          if (e.key === 'Enter') setIsEditing(false);
        }}
        className={`${sharedClasses} ${inputClassName}`}
      />
    );
  }

  if (!isEditMode) {
    return <Tag className={className}>{children}</Tag>;
  }

  // Edit mode but not actively editing — show affordance
  const ringClass = isDirty
    ? 'ring-2 ring-amber-400 ring-offset-1'
    : 'ring-1 ring-dashed ring-green-400/60 hover:ring-green-500';

  return (
    <Tag
      className={`${className} cursor-text rounded ${ringClass} transition-shadow`}
      onClick={() => setIsEditing(true)}
    >
      {children}
    </Tag>
  );
}
